import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Mail, Phone, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [stage, setStage] = useState("contact"); // "contact" | "pin"
  const [via, setVia] = useState("email"); // "email" | "sms" — vilken kanal PIN skickats via
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(null); // null | "email" | "sms"
  const [devPin, setDevPin] = useState(null);
  const requestPin = async (channel) => {
    if (channel === "email") {
      if (!email.includes("@")) {
        toast.error("Ange en giltig e-postadress");
        return;
      }
    } else {
      if (!phone || phone.replace(/\D/g, "").length < 8) {
        toast.error("Ange ett giltigt svenskt mobilnummer");
        return;
      }
    }
    setLoading(channel);
    try {
      const payload = channel === "email" ? { email, via: "email" } : { phone, via: "sms" };
      const { data } = await api.post("/auth/request-pin", payload);
      setVia(channel);
      setStage("pin");
      setDevPin(data.dev_pin || null);
      const dest = channel === "email" ? "din e-post" : "ditt mobilnummer";
      toast.success(
        data.dev_pin
          ? `Dev-läge: PIN är ${data.dev_pin}`
          : `PIN skickad till ${dest}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skicka PIN");
    } finally {
      setLoading(null);
    }
  };

  const verifyPin = async () => {
    if (pin.length !== 6) {
      toast.error("PIN måste vara 6 siffror");
      return;
    }
    setLoading("verify");
    try {
      const payload = via === "email" ? { email, pin } : { phone, pin };
      const { data } = await api.post("/auth/verify-pin", payload);
      setToken(data.token);
      await refresh();
      toast.success(`Välkommen, ${data.user.name}!`);
      nav("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fel kod");
    } finally {
      setLoading(null);
    }
  };

  const destLabel = via === "email" ? email : phone;

  return (
    <div className="app-container pt-safe" data-testid="login-page">
      <div
        className="relative h-56 overflow-hidden"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/47440676-455b-415d-a656-32c4bba91c19/images/e337950c6810be8def3f3e914fbfb2881b2aa07479015766b7eda3c817264b93.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FAF9F6]/30 to-[#FAF9F6]" />
        <div className="relative h-full flex flex-col justify-end px-6 pb-6">
          <p className="label-eyebrow text-[color:var(--text-secondary)]">Välkommen hem</p>
          <h1 className="font-display text-3xl font-medium text-[color:var(--text-primary)] mt-1">
            Familjen
          </h1>
        </div>
      </div>

      <div className="px-6 pt-2 pb-12 fade-up">
        {stage === "contact" ? (
          <div className="space-y-5">
            <div>
              <h2 className="font-display text-xl font-medium">Logga in</h2>
              <p className="text-sm text-[color:var(--text-secondary)] mt-1">
                Vi skickar en 6-siffrig kod till din e-post eller mobil.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-eyebrow block mb-2">E-post</label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-tertiary)]"
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && requestPin("email")}
                    placeholder="namn@exempel.se"
                    className="rounded-xl h-12 pl-9"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <Button
                onClick={() => requestPin("email")}
                disabled={loading === "email"}
                className="w-full h-12 rounded-xl bg-[color:var(--moss)] hover:bg-[#1f421f] text-white font-medium disabled:opacity-100 disabled:bg-[color:var(--moss)]"
                data-testid="login-request-pin-email"
              >
                {loading === "email" ? "Skickar..." : "Skicka PIN via e-post"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[color:var(--border)]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[#FAF9F6] px-3 text-xs uppercase tracking-wider text-[color:var(--text-tertiary)]">
                  Eller
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-eyebrow block mb-2">Mobilnummer</label>
                <div className="relative">
                  <Phone
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-tertiary)]"
                  />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && requestPin("sms")}
                    placeholder="070-123 45 67"
                    className="rounded-xl h-12 pl-9"
                    data-testid="login-phone-input"
                  />
                </div>
              </div>

              <Button
                onClick={() => requestPin("sms")}
                disabled={loading === "sms"}
                variant="outline"
                className="w-full h-12 rounded-xl border-[color:var(--moss)] text-[color:var(--moss)] hover:bg-[color:var(--moss)] hover:text-white font-medium disabled:opacity-100 disabled:border-[color:var(--moss)] disabled:text-[color:var(--moss)]"
                data-testid="login-request-pin-sms"
              >
                {loading === "sms" ? "Skickar..." : "Skicka PIN via SMS"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <h2 className="font-display text-xl font-medium">Ange din kod</h2>
              <p className="text-sm text-[color:var(--text-secondary)] mt-1">
                Vi har skickat en 6-siffrig kod till{" "}
                <span className="font-medium text-[color:var(--text-primary)]">{destLabel}</span>{" "}
                ({via === "email" ? "e-post" : "SMS"})
              </p>
              {devPin && (
                <p className="mt-2 text-xs bg-[color:var(--bg-elevated)] inline-block px-3 py-1 rounded-full font-mono">
                  Dev PIN: <span className="font-semibold">{devPin}</span>
                </p>
              )}
            </div>

            <div className="flex justify-center" data-testid="login-otp-input">
              <InputOTP maxLength={6} value={pin} onChange={setPin}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              onClick={verifyPin}
              disabled={loading !== null || pin.length !== 6}
              className="w-full h-12 rounded-xl bg-[color:var(--moss)] hover:bg-[#1f421f] text-white font-medium"
              data-testid="login-verify-pin-button"
            >
              {loading === "verify" ? "Verifierar..." : "Logga in"}
            </Button>

            <button
              onClick={() => {
                setStage("contact");
                setPin("");
                setDevPin(null);
              }}
              className="text-sm text-[color:var(--text-secondary)] flex items-center gap-1.5 mx-auto"
              data-testid="login-back-button"
            >
              <RotateCcw size={14} />
              Tillbaka
            </button>
          </div>
        )}

        <p className="text-xs text-center text-[color:var(--text-tertiary)] mt-10">
          Bara för familjen. Hanteras varsamt.
        </p>
      </div>
    </div>
  );
}
